import { DeleteOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { AutoComplete, Button, Card, Checkbox, Col, Form, Input, InputNumber, Modal, Row, Table } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import {
  updateField,
  updatePmAdditionalWorkPlaceItem,
} from '../../../../../../contextRedux/PMAdditionalAct/PmAdditionalActModel';
import { RootState } from '../../../../../../store';
import { Column } from '../../../../../../utils/column';
import '../LaborTable/LaborTable.css';

const LaborTable: React.FC<any> = ({}) => {
  const isMobile = useMediaQuery({ query: '(max-width: 992px)' });
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [laborTableData, setLaborTableData] = useState<any>([]);
  const pmAdditionalAct = useSelector((state: RootState) => state?.models?.pmAdditionalAct);
  const addedDevices = useSelector((state: RootState) => state?.models?.pmAdditionalAct.pmContractWorkPlaceItems);
  const laborExtras = useSelector((state: RootState) => state?.store?.laborExtra?.data);
  const unitMeasures = useSelector((state: RootState) => state?.store?.unitMeasure?.data);
  const company = useSelector((state: RootState) =>
    state.store.company.data.find((c) => c.id == state.models.pmAdditionalAct.companyId)
  );
  const workPlaces = company?.workPlaces;

  const [selectedOption, setSelectedOption] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [openIndexes, setOpenIndexes] = useState<number[]>([]);

  useEffect(() => {
    form.setFieldValue('pmContractLaborExtras', pmAdditionalAct.pmContractLaborExtras);
  }, [form, pmAdditionalAct.pmContractLaborExtras]);

  useEffect(() => {
    const pmContractLaborExtras = pmAdditionalAct.pmContractLaborExtras;

    const totalLabor = Number.parseFloat(
      (
        addedDevices.reduce((total, item) => total + item.laborPrice, 0) +
        (pmContractLaborExtras
          ? pmContractLaborExtras.reduce((total: any, item: any) => total + item?.piece * item?.price, 0)
          : 0)
      ).toFixed(2)
    );

    const totalProductsValue = Number.parseFloat(
      addedDevices.reduce((total, item) => {
        const totalPrice = item.price;
        return total + totalPrice;
      }, 0)
    );

    const percent = Number.parseFloat(((totalLabor / (totalLabor + totalProductsValue)) * 100).toFixed(2));

    const updatedLaborTableData = [
      {
        key: 'total',
        totalLabor: totalLabor,
        totalProductsValue: totalProductsValue,
        percent: percent >= 1 ? percent : 1,
        totalLaborAndProducts: Number.parseFloat((totalLabor + totalProductsValue).toFixed(2)),
      },
    ];
    setLaborTableData(updatedLaborTableData);
  }, [addedDevices, pmAdditionalAct.pmContractLaborExtras, form]);

  const handleSelectLabor = (value: string) => {
    const labor = laborExtras.find((date) => date?.name === value);
    const fieldsValue = {
      ...form.getFieldsValue(true),
      laborExtra: labor,
      laborExtraId: labor?.id,
    };
    form.setFieldsValue(fieldsValue);
  };

  const handleSelectWorkPlaceLabor = (value: string) => {
    const workPlace = workPlaces.find((ad: any) => ad?.name == value);

    const fieldsValue = {
      ...form.getFieldsValue(true),
      workPlace: workPlace,
      workPlaceId: workPlace?.id,
    };
    form.setFieldsValue(fieldsValue);
  };

  const handleFilter = (inputValue: string, option: any) => {
    return option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
  };

  const handleCheckboxChange = (option: any) => {
    setSelectedOption(option); // Set the selected option
  };

  const handlePercentageChange = () => {
    const pmContractLaborExtras = pmAdditionalAct.pmContractLaborExtras;

    const totalLabor = Number.parseFloat(
      addedDevices.reduce((total, item) => total + item.laborPrice, 0).toFixed(2) +
        pmContractLaborExtras.reduce((total: any, item: any) => total + item?.piece * item?.price, 0).toFixed(2)
    );

    const totalProductsValue = Number.parseFloat(
      addedDevices
        .reduce((total, item) => {
          const totalPrice = item.price;
          return total + totalPrice;
        }, 0)
        .toFixed(2)
    );

    const percent = Number.parseFloat((laborTableData[0].percent / 100).toFixed(2));
    const a = Number.parseFloat((percent * totalProductsValue).toFixed(2));
    const b = Number.parseFloat((a / (1 - percent)).toFixed(2));
    const c = Number.parseFloat((b / totalLabor).toFixed(2));

    const factor = c;

    const updatedTable = addedDevices?.map((item) => {
      // Create a new copy of the item
      let newItem = { ...item };

      if (item.unitMeasure === 'hour') {
        // Update laborPriceOnHour and laborPrice
        newItem.laborPriceOnHour =
          Number.parseFloat(((item.laborPrice / item.piece) * factor).toFixed(2)) >= 1
            ? Number.parseFloat(((item.laborPrice / item.piece) * factor).toFixed(2))
            : 1;
        newItem.laborPrice = Number.parseFloat((newItem.laborPriceOnHour * item.piece).toFixed(2));
      } else {
        // Deep clone the device object to avoid mutation of the original object
        let newDevice = { ...item.device };

        newDevice.laborPricePM =
          item.laborPrice / item.piece > item.device.laborPriceSM
            ? Number.parseFloat(((item.laborPrice / item.piece) * factor).toFixed(2)) >= 1
              ? Number.parseFloat(((item.laborPrice / item.piece) * factor).toFixed(2))
              : 1
            : Number.parseFloat((item.device.laborPricePM * factor).toFixed(2)) >= 1
            ? Number.parseFloat((item.device.laborPricePM * factor).toFixed(2))
            : 1;

        newItem.device = newDevice; // Assign the new deep-copied device to newItem
        newItem.laborPrice = Number.parseFloat((newDevice.laborPricePM * item.piece).toFixed(2));
      }
      return newItem;
    });

    const updatedLaborExtraItems = pmContractLaborExtras.map((ole: any) => {
      let newItem = { ...ole };
      newItem.price = Number.parseFloat(((newItem.price * factor * newItem.piece) / newItem.piece).toFixed(2));
      newItem.totalManopera = Number.parseFloat((newItem.price * newItem.piece).toFixed(2));

      return newItem;
    });

    dispatch(updateField({ field: 'pmContractLaborExtras', value: updatedLaborExtraItems }));
    form.setFieldValue('pmContractLaborExtras', updatedLaborExtraItems);

    updatedTable.map((ut: any) => {
      dispatch(
        updatePmAdditionalWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === ut.deviceTypeId && item.workPlaceId === ut.workPlaceId
          ),
          updates: ut,
        })
      );
    });
  };

  const handleTotalLaborAndProductsChange = () => {
    const pmContractLaborExtras = pmAdditionalAct.pmContractLaborExtras;

    const totalLabor = Number.parseFloat(
      (
        addedDevices.reduce((total, item) => total + item.laborPrice, 0) +
        (pmContractLaborExtras
          ? pmContractLaborExtras.reduce((total: any, item: any) => total + item?.piece * item?.price, 0)
          : 0)
      ).toFixed(2)
    );

    const totalProductsValue = Number.parseFloat(
      addedDevices
        .reduce((total, item) => {
          const totalPrice = item.price;
          return total + totalPrice;
        }, 0)
        .toFixed(2)
    );

    // Target total values (e.g., set by the user)
    const targetTotal = laborTableData[0].totalLaborAndProducts;

    // Determine factors based on the selected option
    let laborFactor = 1;
    let productFactor = 1;

    if (selectedOption === 'updatePriceWithAddition') {
      productFactor = Number.parseFloat(
        (1 + (targetTotal - (totalLabor + totalProductsValue)) / totalProductsValue).toFixed(4)
      );
    } else if (selectedOption === 'updateLaborPrice') {
      laborFactor = Number.parseFloat((1 + (targetTotal - (totalLabor + totalProductsValue)) / totalLabor).toFixed(4));
    } else if (selectedOption === 'updatePriceWithAdditionAndLaborPrice') {
      // Split the adjustment between labor and products proportionally
      const currentTotal = totalLabor + totalProductsValue;
      const totalDelta = targetTotal - currentTotal;

      const laborRatio = totalLabor / currentTotal;
      const productsRatio = totalProductsValue / currentTotal;

      const laborAdjustment = totalDelta * laborRatio;
      const productsAdjustment = totalDelta * productsRatio;

      laborFactor = Number.parseFloat((1 + laborAdjustment / totalLabor).toFixed(4));
      productFactor = Number.parseFloat((1 + productsAdjustment / totalProductsValue).toFixed(4));
    }

    // Update the devices based on the selected option and calculated factors
    const updatedDevices = addedDevices.map((item) => {
      let updatedItem = { ...item };

      // If updating products' price with addition
      if (selectedOption === 'updatePriceWithAddition' || selectedOption === 'updatePriceWithAdditionAndLaborPrice') {
        const newPrice = Math.max(
          Number.parseFloat((item.price * productFactor).toFixed(2)),
          item.device.price * item.piece
        );

        const newAddition = Number.parseFloat(
          (((newPrice - item.device.price * item.piece) / (item.device.price * item.piece)) * 100).toFixed(2)
        );

        updatedItem.price = newPrice;
        updatedItem.addition = Math.max(newAddition, 1);
      }

      // If updating labor prices
      if (selectedOption === 'updateLaborPrice' || selectedOption === 'updatePriceWithAdditionAndLaborPrice') {
        if (item.unitMeasure === 'hour') {
          const newLaborPriceOnHour = Math.max(
            Number.parseFloat(((item.laborPrice / item.piece) * laborFactor).toFixed(2)),
            1
          );
          updatedItem.laborPriceOnHour = newLaborPriceOnHour;
          updatedItem.laborPrice = Number.parseFloat((newLaborPriceOnHour * item.piece).toFixed(2));
        } else {
          const updatedDevice = { ...item.device };

          updatedDevice.laborPricePM = Math.max(
            Number.parseFloat(((item.laborPrice / item.piece) * laborFactor).toFixed(2)),
            1
          );

          updatedItem.device = updatedDevice;
          updatedItem.laborPrice = Number.parseFloat((updatedDevice.laborPricePM * item.piece).toFixed(2));
        }
      }

      return updatedItem;
    });

    const updatedLaborExtraItems = pmAdditionalAct.pmContractLaborExtras.map((extraItem: any) => {
      const updatedExtra = { ...extraItem };

      updatedExtra.price = Number.parseFloat(
        ((extraItem.price * laborFactor * extraItem.piece) / extraItem.piece).toFixed(2)
      );

      updatedExtra.totalManopera = Number.parseFloat((updatedExtra.price * updatedExtra.piece).toFixed(2));

      return updatedExtra;
    });

    // Update the summary row with the adjusted labor extra items
    dispatch(updateField({ field: 'pmContractLaborExtras', value: updatedLaborExtraItems }));

    // Set updated values in the form and dispatch changes
    form.setFieldValue('pmContractLaborExtras', updatedLaborExtraItems);

    updatedDevices.map((ut: any) => {
      dispatch(
        updatePmAdditionalWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === ut.deviceTypeId && item.workPlaceId === ut.workPlaceId
          ),
          updates: ut,
        })
      );
    });
    setIsModalVisible(false);
  };

  const onChangePercent = (value: number) => {
    let updatedData = [...laborTableData];
    updatedData[0].percent = Number.parseFloat(value != null ? value.toFixed(2) : updatedData[0].percent);

    setLaborTableData(updatedData);
  };

  const onChangeLaborAndProducts = (value: number) => {
    let updatedData = [...laborTableData];
    updatedData[0].totalLaborAndProducts = Number.parseFloat(
      value != null ? value.toFixed(2) : updatedData[0].totalLaborAndProducts
    );

    setLaborTableData(updatedData);
  };

  const handleAdd = () => {
    if (!form) {
      console.error('form is undefined');
      return;
    }

    form
      .validateFields(['workPlaceId', 'workPlace', 'laborExtraId', 'laborExtra', 'unitMeasure', 'piece', 'price'])
      .then(
        (values: {
          workPlaceId: any;
          workPlace: any;
          piece: number;
          price: number;
          laborExtraId: any;
          laborExtra: any;
          unitMeasure: any;
        }) => {
          const currentItems = pmAdditionalAct.pmContractLaborExtras || [];
          const newItem = {
            id: '',
            //totalManopera: values.piece * values.price,
            workPlaceId: values.workPlaceId,
            workPlace: values.workPlace,
            laborExtraId: values.laborExtraId,
            laborExtra: values.laborExtra,
            unitMeasure: values.unitMeasure,
            piece: values.piece,
            price: values.price,
          };
          form.setFieldsValue({
            pmContractLaborExtras: [...currentItems, newItem],
          });

          dispatch(
            updateField({ field: 'pmContractLaborExtras', value: [...pmAdditionalAct.pmContractLaborExtras, newItem] })
          );

          form.resetFields(['workPlaceId', 'laborExtraId', 'unitMeasure', 'piece', 'price']);
        }
      )
      .catch((errorInfo: any) => {
        console.error(errorInfo);
      });
  };

  const handleDeleteTempItem = (indexToRemove: any) => {
    const updatedLaborItems = pmAdditionalAct.pmContractLaborExtras?.filter((_item, index) => index !== indexToRemove);

    const recalculatedLaborItems = updatedLaborItems?.map((item, index) => ({
      ...item,
      key: index,
      totalManopera: item.piece * item.price,
    }));

    dispatch(updateField({ field: 'pmContractLaborExtras', value: recalculatedLaborItems }));
  };

  const handleUpdateTempItem = (key: any, field: string, value: any) => {
    dispatch(
      updateField({
        field: 'pmContractLaborExtras',
        value: pmAdditionalAct.pmContractLaborExtras.map((item) =>
          item.key === key
            ? {
                ...item,
                [field]: value,
                totalManopera:
                  field === 'piece' || field === 'price'
                    ? (field === 'piece' ? value : item.piece) * (field === 'price' ? value : item.price) || 0
                    : item.totalManopera || 0,
              }
            : item
        ),
      })
    );
  };
  const toggleCollapse = (index: number) => {
    setOpenIndexes((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  const renderColumn = (column: any, _text: string, record: any) => {
    const value = Array.isArray(column.dataIndex)
      ? column.dataIndex.reduce((acc: any, key: any) => acc[key], record)
      : record[column.dataIndex];

    if (column.editable) {
      return (
        <>
          {column.dataIndex === 'percent' && (
            <div className='input-button-container'>
              <InputNumber
                min={1}
                value={value}
                formatter={(value) => `${value}%`}
                parser={(value) => value?.replace('%', '')}
                onChange={(value) => onChangePercent(value)}
                className='totalLabor-input'
              />
              <Button
                style={{ marginLeft: '10px' }}
                onClick={() => handlePercentageChange()} // Open modal
                className='totalLabor-applyBtn'
              >
                Aplica
              </Button>
            </div>
          )}
          {column.dataIndex === 'totalLaborAndProducts' && (
            <div className='input-button-container'>
              <InputNumber
                min={1}
                value={value}
                onChange={(value) => onChangeLaborAndProducts(value!)}
                className='totalLabor-input'
              />
              <Button
                style={{ marginLeft: '10px' }}
                onClick={() => setIsModalVisible(true)} // Open modal
                className='totalLabor-applyBtn'
              >
                Aplica
              </Button>

              <Modal
                title='Optiuni de modificare pret'
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)} // Close modal on cancel
                onOk={() => handleTotalLaborAndProductsChange()} // Finalize when "Finalizeza" button is clicked
                okText='Finalizeza'
              >
                <div style={{ display: 'grid' }}>
                  <Checkbox
                    checked={selectedOption === 'updatePriceWithAddition'}
                    onChange={() => handleCheckboxChange('updatePriceWithAddition')}
                  >
                    Updateaza pret cu adaos
                  </Checkbox>
                  <Checkbox
                    checked={selectedOption === 'updateLaborPrice'}
                    onChange={() => handleCheckboxChange('updateLaborPrice')}
                  >
                    Updateaza pret manopera
                  </Checkbox>
                  <Checkbox
                    checked={selectedOption === 'updatePriceWithAdditionAndLaborPrice'}
                    onChange={() => handleCheckboxChange('updatePriceWithAdditionAndLaborPrice')}
                  >
                    Updateaza pret cu adaos si manopera
                  </Checkbox>
                </div>
              </Modal>
            </div>
          )}
        </>
      );
    }
    return value;
  };

  const columnsSecondTable: Column[] = [
    {
      title: 'Procent Manopera Adaugata',
      dataIndex: 'percent',
      key: 'percent',
      editable: true,
    },
    {
      title: 'Total Manopera Adaugata',
      dataIndex: 'totalLabor',
      key: 'totalLabor',
      render: (totalLabor: any) => Number.parseFloat(totalLabor.toFixed(2)) + ' lei',
    },
    {
      title: 'Total Valoare Produse',
      dataIndex: 'totalProductsValue',
      key: 'totalProductsValue',
      render: (totalProductsValue: any) => Number.parseFloat(totalProductsValue.toFixed(2)) + ' lei',
    },
    {
      title: 'Total Manopera + Produse',
      dataIndex: 'totalLaborAndProducts',
      key: 'totalLaborAndProducts',
      editable: true,
    },
  ];

  return (
    <div>
      {isMobile ? (
        // Mobile View
        laborTableData.map((item: any, index: number) => (
          <Card key={index} className='labor-table-data-total'>
            {columnsSecondTable.map((column) => (
              <div key={column.key} className='labor-table-data-container'>
                <strong>{column.title}:</strong>
                <span>{renderColumn(column, '', item)}</span>
              </div>
            ))}
          </Card>
        ))
      ) : (
        <Table
          rowClassName={'table-background-color'}
          dataSource={laborTableData}
          rowHoverable={false}
          columns={columnsSecondTable?.map((column, index) => ({
            ...column,
            key: index,
            render: column.render || ((text, record) => renderColumn(column, text, record)),
          }))}
          pagination={false}
        />
      )}

      {/* Form 1 */}
      <Form form={form} layout='vertical'>
        <div className=' labor-table-exatra-add'>
          <Row gutter={[16, 16]} align={'middle'}>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item
                name={['workPlace', 'name']}
                label='Punct de lucru'
                rules={[{ required: true, message: 'Please select the workPlace' }]}
              >
                <AutoComplete onSelect={handleSelectWorkPlaceLabor}>
                  {workPlaces?.map((workPlace: any, index: any) => (
                    <AutoComplete.Option key={index} value={workPlace?.name}>
                      {workPlace?.name}
                    </AutoComplete.Option>
                  ))}
                </AutoComplete>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item
                name={['pmContractLaborExtras', 'laborExtra', 'name']}
                label='Manopera'
                rules={[{ required: true, message: 'Please input manopera' }]}
              >
                <AutoComplete onSelect={handleSelectLabor} filterOption={handleFilter}>
                  {laborExtras?.map((item, index) => (
                    <AutoComplete.Option key={index} value={item?.name}>
                      {item?.name}
                    </AutoComplete.Option>
                  ))}
                </AutoComplete>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item
                name='unitMeasure'
                label='Unitate de masura'
                rules={[{ required: true, message: 'Please input unitate de masura' }]}
              >
                <AutoComplete filterOption={handleFilter}>
                  {unitMeasures?.map((item: any, index: number) => (
                    <AutoComplete.Option key={index} value={item}>
                      {item}
                    </AutoComplete.Option>
                  ))}
                </AutoComplete>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item name='piece' label='Cantitate' rules={[{ required: true, message: 'Please input cantitate' }]}>
                <InputNumber min={1} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item name='price' label='Pret' rules={[{ required: true, message: 'Please input pret' }]}>
                <InputNumber min={1} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Button type='primary' onClick={handleAdd} style={{ width: '100%' }}>
                Add
              </Button>
            </Col>
          </Row>
        </div>
      </Form>

      {/* form 2 */}
      <Form form={form} layout='vertical'>
        <Form.List name='pmContractLaborExtras'>
          {(fields, { remove }) => (
            <>
              {fields?.map(({ key, name, ...restField }, index) =>
                //Mobile view
                isMobile ? (
                  <Card
                    className='pmContractLaborExtras'
                    key={key}
                    title={
                      <div className='laborExtras-title'>
                        <Button type='link' onClick={() => toggleCollapse(index)}>
                          {openIndexes.includes(index) ? <UpOutlined /> : <DownOutlined />}
                          <span>
                            <strong>Contract Labor Extras - {index + 1}</strong>
                          </span>
                        </Button>
                        <div>
                          <Button
                            onClick={() => {
                              handleDeleteTempItem(index);
                              remove(key);
                            }}
                            className='laborTable-delete-btn-sm'
                          >
                            <DeleteOutlined />
                          </Button>
                        </div>
                      </div>
                    }
                  >
                    {openIndexes.includes(index) && (
                      <Row gutter={[8, 8]} style={{ padding: 0 }}>
                        {[
                          { name: 'workPlace.name', label: 'Punct de lucru' },
                          { name: 'laborExtra.name', label: 'Manopera' },
                          { name: 'unitMeasure', label: 'Unitate de masura' },
                        ].map((field, i) => (
                          <Col xs={24} sm={12} md={8} lg={4} key={i}>
                            <Form.Item {...restField} name={[name, ...field.name.split('.')]} label={field.label}>
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        ))}
                        {[
                          { name: 'piece', label: 'Cantitate' },
                          { name: 'price', label: 'Pret' },
                        ].map((field, i) => (
                          <Col xs={24} sm={12} md={8} lg={4} key={i}>
                            <Form.Item {...restField} name={[name, field.name]} label={field.label}>
                              <InputNumber disabled min={1} />
                            </Form.Item>
                          </Col>
                        ))}
                      </Row>
                    )}
                  </Card>
                ) : (
                  //Desktop view
                  <div key={key} className=' labor-table-exatra-add'>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item {...restField} name={[name, 'workPlace', 'name']}>
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item {...restField} name={[name, 'laborExtra', 'name']}>
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item {...restField} name={[name, 'unitMeasure']}>
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item {...restField} name={[name, 'piece']}>
                          <InputNumber
                            disabled
                            min={1}
                            onChange={(value) => handleUpdateTempItem(index, 'piece', value)}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item {...restField} name={[name, 'price']}>
                          <InputNumber
                            disabled
                            min={1}
                            onChange={(value) => handleUpdateTempItem(index, 'price', value)}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={4}>
                        <Button
                          danger
                          className='laborTable-delete-btn-lg'
                          onClick={() => {
                            handleDeleteTempItem(index);
                            remove(key);
                          }}
                        >
                          Delete
                        </Button>
                      </Col>
                    </Row>
                  </div>
                )
              )}
            </>
          )}
        </Form.List>
      </Form>
    </div>
  );
};

export default LaborTable;
